import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, Bug, Cloud, Database, DownloadSimple, Key, MagnifyingGlass, ShieldCheck, User, UserPlus, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api, formatErr } from "@/lib/api";
import { langLabel } from "@/lib/languages";

export default function AdminPage() {
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [userProjects, setUserProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [pwModalUser, setPwModalUser] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return users;
    return users.filter((u) =>
      (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q)
    );
  }, [users, search]);

  const selectUser = async (u) => {
    setSelected(u);
    setProjectsLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${u.user_id}/projects`);
      setUserProjects(data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
      setUserProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  };

  const toggleRole = async (u) => {
    const next = u.role === "admin" ? "editor" : "admin";
    if (!window.confirm(`Change ${u.email} role to ${next.toUpperCase()}?`)) return;
    try {
      const { data } = await api.patch(`/admin/users/${u.user_id}`, { role: next });
      setUsers((arr) => arr.map((x) => (x.user_id === u.user_id ? { ...x, role: data.role } : x)));
      if (selected?.user_id === u.user_id) setSelected({ ...selected, role: data.role });
      toast.success(`Role updated to ${data.role}`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const [importing, setImporting] = useState(false);
  const importKeys = async (force = false) => {
    if (!window.confirm(
      force
        ? "FORCE: this will OVERWRITE your current admin STT keys with any keys found on other user accounts. Continue?"
        : "Scan all user accounts and merge any STT keys they have into the admin (platform-wide). Blank admin fields fill first; conflicting values are skipped."
    )) return;
    setImporting(true);
    try {
      const { data } = await api.post(`/admin/import-stt-keys${force ? "?force=true" : ""}`);
      if (!data.imported_from?.length) {
        toast.success("Nothing to import — every STT key is already on the admin account.");
      } else {
        toast.success(`Imported ${data.imported_keys.length} key(s) from ${data.imported_from.length} user(s)`);
        if (data.skipped_keys?.length) {
          toast.message(`Skipped (already set on admin): ${data.skipped_keys.join(", ")}`);
        }
      }
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <AppShell>
      <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6 mb-10">
        <div>
          <div className="overline text-[--muted] mb-3 inline-flex items-center gap-2">
            <ShieldCheck weight="bold" size={14} /> ADMIN CONSOLE
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Users & access</h1>
          <p className="mt-3 text-[--muted] max-w-xl">
            Manage roles, reset passwords, and inspect any user's projects.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => setShowCreate(true)}
              data-testid="admin-create-user-btn"
              className="bg-[--accent] text-white px-3 py-2 text-xs font-bold inline-flex items-center gap-2 hover:bg-[--accent-hi] transition-colors"
              title="Create a new user (admin or editor)"
            >
              <UserPlus weight="bold" size={12} /> Create user
            </button>
            <Link
              to="/admin/database"
              data-testid="admin-database-link"
              className="border border-[--ink] px-3 py-2 text-xs font-bold inline-flex items-center gap-2 hover:bg-[--ink] hover:text-white transition-colors"
              title="Backup and restore the MongoDB database"
            >
              <Database weight="bold" size={12} /> Database management
            </Link>
            <Link
              to="/admin/storage"
              data-testid="admin-storage-link"
              className="border border-[--ink] px-3 py-2 text-xs font-bold inline-flex items-center gap-2 hover:bg-[--ink] hover:text-white transition-colors"
              title="Switch between Emergent / local filesystem / Azure Blob"
            >
              <Cloud weight="bold" size={12} /> Storage
            </Link>
            <Link
              to="/admin/errors"
              data-testid="admin-errors-link"
              className="border border-[--ink] px-3 py-2 text-xs font-bold inline-flex items-center gap-2 hover:bg-[--ink] hover:text-white transition-colors"
              title="Inspect logged backend errors and tracebacks"
            >
              <Bug weight="bold" size={12} /> Error log
            </Link>
            <button
              onClick={() => importKeys(false)}
              disabled={importing}
              data-testid="import-stt-keys-btn"
              className="border border-[--ink] px-3 py-2 text-xs font-bold inline-flex items-center gap-2 hover:bg-[--ink] hover:text-white transition-colors disabled:opacity-50"
              title="Pull STT keys saved on user accounts into the admin (platform-wide). Blank admin fields filled first."
            >
              <DownloadSimple weight="bold" size={12} />
              {importing ? "Importing…" : "Import STT keys from users"}
            </button>
            <button
              onClick={() => importKeys(true)}
              disabled={importing}
              data-testid="import-stt-keys-force-btn"
              className="border border-[--ink] px-3 py-2 text-xs font-bold inline-flex items-center gap-2 hover:bg-[--accent] hover:text-white hover:border-[--accent] transition-colors disabled:opacity-50"
              title="DANGER: overwrite admin's current keys with whatever values are found on user accounts"
            >
              <DownloadSimple weight="bold" size={12} /> Force overwrite
            </button>
          </div>
        </div>
        <div className="relative w-full sm:w-80">
          <MagnifyingGlass weight="bold" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[--muted]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email or name…"
            data-testid="admin-search-input"
            className="w-full border border-[--ink] pl-9 pr-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
          />
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-px bg-[--line] border border-[--line]">
        <div className="lg:col-span-2 bg-white">
          {loading ? (
            <div className="p-6 overline text-[--muted]">Loading users…</div>
          ) : filtered.length === 0 ? (
            <div className="p-6 overline text-[--muted]">No users match.</div>
          ) : (
            <div className="divide-y divide-[--line]">
              {filtered.map((u) => (
                <div
                  key={u.user_id}
                  className={`p-5 flex items-center gap-4 hover:bg-[--paper] transition-colors ${selected?.user_id === u.user_id ? "bg-[--paper]" : ""}`}
                  data-testid="admin-user-row"
                >
                  <div className="w-10 h-10 grid place-items-center bg-[--ink] text-white font-mono text-sm shrink-0">
                    {(u.name || u.email || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm truncate">{u.name || u.email}</span>
                      {u.role === "admin" && (
                        <span className="text-[10px] font-mono px-1.5 py-0.5 bg-[--ink] text-white">ADMIN</span>
                      )}
                      <span className="text-[10px] font-mono text-[--muted]">{u.provider || "email"}</span>
                    </div>
                    <div className="text-xs font-mono text-[--muted] truncate">{u.email}</div>
                    <div className="text-xs font-mono text-[--muted] mt-0.5">{u.projects_count || 0} project{u.projects_count === 1 ? "" : "s"}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                    <button
                      onClick={() => selectUser(u)}
                      data-testid="admin-view-projects-btn"
                      className="border border-[--ink] px-3 py-2 text-xs font-semibold hover:bg-[--ink] hover:text-white transition-colors inline-flex items-center gap-1"
                    >
                      <User weight="bold" size={12} /> Projects
                    </button>
                    <button
                      onClick={() => toggleRole(u)}
                      data-testid="admin-toggle-role-btn"
                      className="border border-[--ink] px-3 py-2 text-xs font-semibold hover:bg-[--ink] hover:text-white transition-colors"
                    >
                      {u.role === "admin" ? "Demote" : "Promote"}
                    </button>
                    {u.provider !== "google" && (
                      <button
                        onClick={() => setPwModalUser(u)}
                        data-testid="admin-reset-password-btn"
                        className="border border-[--ink] px-3 py-2 text-xs font-semibold hover:bg-[--ink] hover:text-white transition-colors inline-flex items-center gap-1"
                      >
                        <Key weight="bold" size={12} /> Password
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white p-6">
          {!selected ? (
            <div className="text-sm text-[--muted]">Select a user to inspect their projects.</div>
          ) : (
            <div>
              <div className="overline text-[--muted] mb-2">USER PROJECTS</div>
              <div className="font-display font-black text-2xl tracking-tighter mb-1">{selected.name || selected.email}</div>
              <div className="text-xs font-mono text-[--muted] mb-5">{selected.email}</div>
              {projectsLoading ? (
                <div className="overline text-[--muted]">Loading…</div>
              ) : userProjects.length === 0 ? (
                <div className="text-sm text-[--muted]">No projects.</div>
              ) : (
                <ul className="space-y-3">
                  {userProjects.map((p) => (
                    <li key={p.project_id} className="border border-[--line] p-3 hover:bg-[--paper] transition-colors">
                      <button
                        className="w-full text-left"
                        onClick={() => nav(`/project/${p.project_id}`)}
                        data-testid="admin-open-project-btn"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-sm truncate">{p.title}</span>
                          <ArrowRight weight="bold" size={12} />
                        </div>
                        <div className="font-mono text-[11px] text-[--muted] mt-1">
                          {langLabel(p.source_language)} · {p.status?.toUpperCase()} · {p.translations_count} translations
                        </div>
                        {p.tags?.length > 0 && (
                          <div className="mt-1 flex gap-1 flex-wrap">
                            {p.tags.map((t) => (
                              <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 bg-[--paper] border border-[--line]">{t}</span>
                            ))}
                          </div>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      {pwModalUser && (
        <PasswordResetModal
          user={pwModalUser}
          onClose={() => setPwModalUser(null)}
          onDone={() => setPwModalUser(null)}
        />
      )}
      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={async () => { setShowCreate(false); await load(); }}
        />
      )}
    </AppShell>
  );
}

function CreateUserModal({ onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("editor");
  const [sendInvite, setSendInvite] = useState(true);
  const [busy, setBusy] = useState(false);

  const generatePassword = () => {
    // Readable 14-char password: 2 words + 4 digits.
    const words = ["solar", "river", "quartz", "amber", "willow", "ember", "delta", "vivid", "swift", "ocean"];
    const w1 = words[Math.floor(Math.random() * words.length)];
    const w2 = words[Math.floor(Math.random() * words.length)];
    const n = Math.floor(1000 + Math.random() * 9000);
    setPassword(`${w1}-${w2}-${n}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users", {
        email: email.trim().toLowerCase(),
        name: name.trim() || null,
        password,
        role,
        send_invite: sendInvite,
      });
      if (sendInvite && data.invite_sent) {
        toast.success(`User created. Invite email sent to ${data.email}`);
      } else if (sendInvite && !data.invite_sent) {
        toast.message(`User created — but invite email could not be sent. Share the password manually.`, {
          description: data.invite_error || "Configure Resend in Settings to enable invites.",
          duration: 8000,
        });
      } else {
        toast.success(`User ${data.email} created`);
      }
      onCreated?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[--ink]/60 backdrop-blur-sm grid place-items-center z-50 p-4 overflow-y-auto" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white hard-border max-w-lg w-full p-8 my-8"
        data-testid="create-user-modal"
      >
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="overline text-[--muted]">CREATE USER</div>
            <h2 className="font-display font-black text-2xl tracking-tighter mt-1 inline-flex items-center gap-2">
              <UserPlus weight="duotone" size={28} /> Invite teammate
            </h2>
          </div>
          <button type="button" onClick={onClose} className="border border-[--ink] p-2 hover:bg-[--ink] hover:text-white transition-colors" aria-label="Close">
            <X weight="bold" size={14} />
          </button>
        </div>

        <div className="space-y-4 mt-5">
          <div>
            <label className="overline block mb-2">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="create-user-email"
              className="w-full border border-[--ink] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
              placeholder="user@studio.com"
              autoFocus
            />
          </div>
          <div>
            <label className="overline block mb-2">Display name (optional)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="create-user-name"
              className="w-full border border-[--ink] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
              placeholder="Maya Singh"
            />
          </div>
          <div>
            <label className="overline block mb-2">Temporary password</label>
            <div className="flex">
              <input
                type="text"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="create-user-password"
                className="flex-1 border border-[--ink] px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[--accent]"
                placeholder="min 6 chars"
              />
              <button
                type="button"
                onClick={generatePassword}
                data-testid="create-user-gen-password"
                className="border border-l-0 border-[--ink] px-3 text-xs font-bold hover:bg-[--ink] hover:text-white transition-colors"
              >
                GENERATE
              </button>
            </div>
            <p className="text-[11px] text-[--muted] font-mono mt-1">Tell the user to change it after first sign-in.</p>
          </div>
          <div>
            <label className="overline block mb-2">Role</label>
            <div className="grid grid-cols-2 gap-px bg-[--line] border border-[--ink]">
              <button type="button" onClick={() => setRole("editor")} data-testid="create-user-role-editor" className={`py-3 text-xs font-bold ${role === "editor" ? "bg-[--accent] text-white" : "bg-white hover:bg-[--paper]"}`}>
                EDITOR
              </button>
              <button type="button" onClick={() => setRole("admin")} data-testid="create-user-role-admin" className={`py-3 text-xs font-bold ${role === "admin" ? "bg-[--accent] text-white" : "bg-white hover:bg-[--paper]"}`}>
                ADMIN
              </button>
            </div>
            <p className="text-[11px] text-[--muted] font-mono mt-1">
              {role === "admin" ? "Admins can manage settings, users, storage and backups." : "Editors can create/manage their own projects."}
            </p>
          </div>
          <label className="flex items-start gap-3 cursor-pointer" data-testid="create-user-send-invite">
            <input
              type="checkbox"
              checked={sendInvite}
              onChange={(e) => setSendInvite(e.target.checked)}
              className="mt-1"
            />
            <span className="text-xs leading-relaxed text-[--muted]">
              Send invite email with the temporary password and a login link. <strong className="text-[--ink]">Requires Resend</strong> to be configured in Settings.
            </span>
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-6">
          <button type="button" onClick={onClose} disabled={busy} className="border border-[--ink] px-4 py-2 text-xs font-bold hover:bg-[--paper] disabled:opacity-50">Cancel</button>
          <button type="submit" disabled={busy} data-testid="create-user-submit" className="bg-[--accent] text-white px-5 py-2 text-xs font-bold inline-flex items-center gap-2 hover:bg-[--accent-hi] transition-colors disabled:opacity-50">
            {busy ? "Creating…" : (<><UserPlus weight="bold" size={14} /> Create user</>)}
          </button>
        </div>
      </form>
    </div>
  );
}

function PasswordResetModal({ user, onClose, onDone }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.user_id}`, { password: pw });
      toast.success(`Password updated for ${user.email}`);
      onDone();
    } catch (e2) {
      toast.error(formatErr(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-[--ink]/60 backdrop-blur-sm grid place-items-center z-50 p-4" onClick={onClose}>
      <div className="bg-white hard-border max-w-md w-full p-8" onClick={(e) => e.stopPropagation()} data-testid="admin-password-modal">
        <div className="overline text-[--muted] mb-2">RESET PASSWORD</div>
        <h2 className="font-display font-black text-2xl tracking-tighter mb-1">{user.name || user.email}</h2>
        <p className="text-xs font-mono text-[--muted] mb-5">{user.email}</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="overline block mb-2">New password</label>
            <input
              type="text"
              autoFocus
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              data-testid="admin-new-password-input"
              className="w-full border border-[--ink] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
              placeholder="At least 6 characters"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-3 text-sm font-semibold hover:text-[--accent]">Cancel</button>
            <button
              type="submit"
              disabled={busy || pw.length < 6}
              data-testid="admin-password-submit"
              className="bg-[--accent] text-white px-5 py-3 text-sm font-bold hover:bg-[--accent-hi] transition-colors disabled:opacity-50"
            >
              {busy ? "Saving…" : "Update password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
